import React, { useState, useEffect, useCallback } from "react";
import {
  List,
  Button,
  message,
  Spin,
  DatePicker,
  Row,
  Col,
  Tooltip,
  Modal,
  Card,
} from "antd";
import {
  CalendarOutlined,
  LeftOutlined,
  RightOutlined,
  EyeOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Description,
  EventNote,
  ArrowBack,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import logo from "../../../Assets/img/logo.webp";
import {
  UnifiedContainer,
  UnifiedLogo,
  UnifiedCardLight,
  UnifiedTitle,
  UnifiedIconButton,
  UnifiedLoadingContainer,
  CenteredLayout,
  colors,
  fadeIn
} from "../../../theme/SharedDesign";
import { styled } from '@mui/material/styles';
import locale from "antd/es/date-picker/locale/es_ES";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { useUser } from "../../../UserContext";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";

const API_URL = process.env.REACT_APP_API_URL;

// Navegación de fecha con estilo claro
const DateNavigation = styled(Box)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  border: '2px solid rgba(255, 255, 255, 0.3)',
  borderRadius: 12,
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  boxShadow: '0 8px 25px rgba(10, 46, 92, 0.15)',
  
  '& .ant-picker': {
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    borderColor: 'rgba(10, 46, 92, 0.3)',
    borderRadius: 8,
    height: 40,
    
    '&:hover, &.ant-picker-focused': {
      borderColor: 'rgba(10, 46, 92, 0.5)',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      boxShadow: '0 0 15px rgba(10, 46, 92, 0.2)',
    },
    
    '& .ant-picker-input > input': {
      color: '#0a2e5c !important',
      fontWeight: 500,
      fontSize: '0.9rem',
    },
    
    '& .ant-picker-suffix': {
      color: 'rgba(10, 46, 92, 0.7)',
    },
  },
  
  '& .ant-btn': {
    backgroundColor: 'rgba(10, 46, 92, 0.1)',
    borderColor: 'rgba(10, 46, 92, 0.3)',
    color: '#0a2e5c',
    height: 40,
    width: 40,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    
    '&:hover': {
      backgroundColor: 'rgba(10, 46, 92, 0.2)',
      borderColor: 'rgba(10, 46, 92, 0.5)',
      transform: 'scale(1.05)',
      boxShadow: '0 4px 15px rgba(10, 46, 92, 0.2)',
    },
  },
}));

// Card moderno con estilo claro
const ModernCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  border: '2px solid rgba(255, 255, 255, 0.3)',
  borderRadius: 20,
  marginBottom: theme.spacing(2),
  boxShadow: '0 8px 25px rgba(10, 46, 92, 0.15)',
  transition: 'all 0.3s ease',
  animation: `${fadeIn} 0.5s ease`,
  position: 'relative',
  overflow: 'hidden',
  
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 15px 35px rgba(10, 46, 92, 0.25)',
    borderColor: 'rgba(255, 255, 255, 0.5)',
  },
  
  '& .ant-card-body': {
    padding: '20px',
    position: 'relative',
    zIndex: 1,
  },
}));

// Header del card con estilo claro
const CardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
  position: 'relative',
  
  '& .date-info': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    position: 'relative',
    padding: theme.spacing(0.5),
    
    '&::before': {
      content: '""',
      position: 'absolute',
      left: -theme.spacing(1),
      top: '50%',
      transform: 'translateY(-50%)',
      width: '3px',
      height: '70%',
      background: 'linear-gradient(to bottom, rgba(10, 46, 92, 0.6), rgba(10, 46, 92, 0.3))',
      borderRadius: '2px',
    },
  },
  
  '& .date-text': {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#0a2e5c',
    letterSpacing: '0.5px',
    position: 'relative',
    
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: -2,
      left: 0,
      width: '100%',
      height: '1px',
      background: 'linear-gradient(90deg, transparent, rgba(10, 46, 92, 0.5), transparent)',
    },
  },
  
  '& .time-text': {
    fontSize: '0.9rem',
    color: 'rgba(10, 46, 92, 0.8)',
    fontWeight: 600,
    letterSpacing: '0.3px',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    
    '&::before': {
      content: '""',
      display: 'inline-block',
      width: '6px',
      height: '6px',
      borderRadius: '50%',
      background: 'rgba(10, 46, 92, 0.6)',
      boxShadow: '0 0 10px rgba(10, 46, 92, 0.3)',
    },
  },
  
  '& .doc-icon': {
    fontSize: '1.4rem',
    color: '#0a2e5c',
    filter: 'drop-shadow(0 0 8px rgba(10, 46, 92, 0.3))',
    transition: 'all 0.3s ease',
    
    '&:hover': {
      transform: 'scale(1.1)',
      filter: 'drop-shadow(0 0 12px rgba(10, 46, 92, 0.5))',
    },
  },
}));

// Botones de acción con estilo claro
const ActionButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  justifyContent: 'center',
  marginTop: theme.spacing(2),
  position: 'relative',
  
  '& .ant-btn': {
    flex: 1,
    padding: theme.spacing(1.5),
    borderRadius: 12,
    fontSize: '0.9rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 1,
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    background: 'linear-gradient(135deg, #0a2e5c, #051b38)',
    color: 'white',
    boxShadow: '0 4px 15px rgba(10, 46, 92, 0.3)',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 20px rgba(10, 46, 92, 0.4)',
      background: 'linear-gradient(135deg, #051b38, #082340)',
    },
    
    '&:active': {
      transform: 'translateY(0)',
    },
    
    '& .anticon': {
      fontSize: '1.1rem',
      transition: 'all 0.3s ease',
    },
  },
}));

// Estado vacío con estilo claro
const EmptyState = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(6),
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  border: '2px solid rgba(255, 255, 255, 0.3)',
  borderRadius: 20,
  boxShadow: '0 8px 25px rgba(10, 46, 92, 0.15)',
  animation: `${fadeIn} 0.6s ease`,
  
  '& .empty-icon': {
    fontSize: '4rem',
    color: 'rgba(10, 46, 92, 0.6)',
    marginBottom: theme.spacing(3),
    filter: 'drop-shadow(0 0 10px rgba(10, 46, 92, 0.2))',
  },
  
  '& .empty-text': {
    color: 'rgba(10, 46, 92, 0.8)',
    fontSize: '1.1rem',
    fontWeight: 500,
    letterSpacing: '0.5px',
    lineHeight: 1.5,
    maxWidth: '80%',
    margin: '0 auto',
  },
}));

// Loading con estilo claro
const CleanLoading = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(6),
  gap: theme.spacing(3),
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  border: '2px solid rgba(255, 255, 255, 0.3)',
  borderRadius: 20,
  boxShadow: '0 8px 25px rgba(10, 46, 92, 0.15)',
  position: 'relative',
  overflow: 'hidden',
  
  '& .ant-spin': {
    '& .ant-spin-dot': {
      '& .ant-spin-dot-item': {
        backgroundColor: '#0a2e5c',
      },
    },
  },
  
  '& .loading-text': {
    color: 'rgba(10, 46, 92, 0.8)',
    fontSize: '1rem',
    fontWeight: 500,
    letterSpacing: '0.5px',
  },
}));

const HistoryMobile = () => {
  const { user } = useUser();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [pdfModalVisible, setPdfModalVisible] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const layoutPlugin = defaultLayoutPlugin();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    setSelectedDate(dayjs());
  }, []);

  const fetchHistorial = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("accessToken");
      const fecha = selectedDate.format("YYYY-MM-DD");
      const res = await fetch(
        `${API_URL}/conductor/pre-operaciones/respuestas?fecha=${fecha}&conductorId=${user.id}`,
        { headers: { Authorization: token ? `Bearer ${token}` : "" } }
      );
      if (!res.ok) throw new Error("Error al cargar historial");
      const json = await res.json();
      setData(json);
    } catch (error) {
      message.error(error.message || "Error al cargar historial");
    } finally {
      setLoading(false);
    }
  }, [selectedDate, user]);

  useEffect(() => {
    fetchHistorial();
  }, [fetchHistorial]);

  const navigateDate = (days) => {
    const newDate = selectedDate.add(days, "day");
    if (newDate.isAfter(dayjs())) {
      message.warning("No puedes seleccionar una fecha futura.");
      return;
    }
    setSelectedDate(newDate);
  };

  const handlePDFAction = async (record, action) => {
    try {
      const token = localStorage.getItem("accessToken");
      const res = await fetch(
        `${API_URL}/conductor/pre-operaciones/respuestas/pdf/${record.pdfFileName}`,
        { headers: { Authorization: token ? `Bearer ${token}` : "" } }
      );
      if (!res.ok) throw new Error("Error al obtener PDF");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);

      if (action === "view") {
        setPdfUrl(url);
        setPdfModalVisible(true);
      } else {
        const link = document.createElement("a");
        link.href = url;
        link.download = record.pdfFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      message.error(
        error.message ||
          `Error al ${action === "view" ? "ver" : "descargar"} PDF`
      );
    }
  };

  const handleModalClose = () => {
    setPdfModalVisible(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl("");
    }
  };

  const handleBack = () => {
    navigate("/modules/Dashboard");
  };


  return (
    <UnifiedContainer>
      <CenteredLayout isMobile={isMobile} maxWidth={600}>
        <UnifiedLogo src={logo} alt="Mogotax Logo" isMobile={isMobile} />
        
        <UnifiedCardLight sx={{ width: '100%', position: 'relative', zIndex: 1 }} isMobile={isMobile}>
          <UnifiedIconButton onClick={handleBack} size={isMobile ? 'medium' : 'large'}>
            <ArrowBack />
          </UnifiedIconButton>

          <UnifiedTitle variant="h4" isMobile={isMobile}>
            <EventNote sx={{ fontSize: isMobile ? '1.6rem' : '2rem', color: '#0a2e5c' }} />
            <span style={{ color: '#0a2e5c' }}>Mis PreOperacionales</span>
          </UnifiedTitle>

        {/* Navegación de fecha */}
        <DateNavigation>
          <Row justify="space-between" align="middle" gutter={12}>
            <Col>
              <Tooltip title="Anterior">
                <Button
                  onClick={() => navigateDate(-1)}
                  icon={<LeftOutlined />}
                  shape="circle"
                />
              </Tooltip>
            </Col>
            <Col flex="1">
              <DatePicker
                value={selectedDate}
                onChange={(date) => date && setSelectedDate(date)}
                format="DD/MM/YYYY"
                locale={locale}
                allowClear={false}
                suffixIcon={<CalendarOutlined />}
                style={{ width: '100%' }}
                disabledDate={(current) =>
                  current && current > dayjs().endOf("day")
                }
                placeholder="Seleccionar fecha"
              />
            </Col>
            <Col>
              <Tooltip title="Siguiente">
                <Button
                  onClick={() => navigateDate(1)}
                  icon={<RightOutlined />}
                  shape="circle"
                />
              </Tooltip>
            </Col>
          </Row>
        </DateNavigation>

        {/* Lista de registros */}
        {loading ? (
          <CleanLoading>
            <Spin size="default" />
            <Typography className="loading-text">
              Cargando registros...
            </Typography>
          </CleanLoading>
        ) : (
          <Box>
            {data.length === 0 ? (
              <EmptyState>
                <Description className="empty-icon" />
                <Typography className="empty-text">
                  No hay registros para esta fecha
                </Typography>
              </EmptyState>
            ) : (
              <List
                dataSource={data}
                renderItem={(item) => (
                  <List.Item style={{ padding: 0 }}>
                    <ModernCard>
                      <CardHeader>
                        <Box className="date-info">
                          <Description className="doc-icon" />
                          <Box>
                            <Typography className="date-text">
                              {dayjs(item.fecha).format("DD/MM/YYYY")}
                            </Typography>
                            <Typography className="time-text">
                              {item.hora}
                            </Typography>
                          </Box>
                        </Box>
                      </CardHeader>
                      
                      <ActionButtons>
                        <Button
                          icon={<EyeOutlined />}
                          onClick={() => handlePDFAction(item, "view")}
                        >
                          Ver
                        </Button>
                        <Button
                          icon={<DownloadOutlined />}
                          onClick={() => handlePDFAction(item, "download")}
                        >
                          Descargar
                        </Button>
                      </ActionButtons>
                    </ModernCard>
                  </List.Item>
                )}
              />
            )}
          </Box>
        )}

        {/* Modal para PDF */}
        <Modal
          open={pdfModalVisible}
          onCancel={handleModalClose}
          footer={null}
          width="100%"
          style={{ top: 0 }}
          styles={{
            body: { padding: 0, height: "80vh", overflow: "hidden" },
          }}
          destroyOnClose
        >
          {pdfUrl && (
            <Worker
              workerUrl={`//unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`}
            >
              <Viewer fileUrl={pdfUrl} plugins={[layoutPlugin]} />
            </Worker>
          )}
        </Modal>
        </UnifiedCardLight>
      </CenteredLayout>
    </UnifiedContainer>
  );
};

export default HistoryMobile;
